import useOutbrainAds from '../../composables/ad-ops/useOutbrainAds'
import debug from '../../utils/console'

const logger = debug.extend('adFunctions')

interface Slot {
  getSlotElementId: () => string
}

export type CreateHook = (callback: () => void, ref?: unknown) => void

export interface AdSetupConfig {
  onMounted: CreateHook
  onUnmounted: CreateHook
}

export interface AdEventRegistry {
  addEventListener: () => void
  removeEventListener: () => void
}

interface Event {
  slot: Slot
  isEmpty: boolean
  slotContentChanged: boolean
  isBackfill: boolean
}

export function setupNativeAdAsFallback(conf?: AdSetupConfig): AdEventRegistry {
  // This function is used to render a native ad in case the Adx script returns empty.
  function renderNativeAd(placementElement: Element, placementClass: string) {
    try {
      if (!placementElement) {
        logger.error('Element not found for native ad fallback', placementClass)
        return
      }

      if (placementElement && placementElement instanceof HTMLElement) {
        const { renderFallbackOutbrainAd } = useOutbrainAds()

        renderFallbackOutbrainAd(placementClass, placementElement)
      }
    } catch (err) {
      logger.error(
        'Error on Gtag slotRenderEnded listener for native ad fallback',
        err
      )
    }
  }

  function renderNativeAdFallback(event: Event) {
    const elementId = event.slot.getSlotElementId() // Element id for Adx script
    const placementElement = document.getElementById(elementId)?.parentElement
    const placementClass = elementId.replace(/__ayManagerEnv__.+$/, '') // Strip away "__ayManagerEnv__XX..." suffix off the element ID to get the placement class
    if (event.isEmpty && placementElement) {
      window.ayManagerEnv.cmd.push(function () {
        window.ayManagerEnv.destroy([elementId], {
          useInstanceCodes: true,
        })
      })
      renderNativeAd(placementElement, placementClass)
    }
  }

  function addEventListener() {
    if (!import.meta.client) return

    try {
      window.googletag = window.googletag || { cmd: [] }
      window.googletag.cmd.push(function () {
        if (!window.googletag.pubads) {
          logger.error('addEventListener: Gtag pubads not found')
          return
        }
        window.googletag
          .pubads()
          // Watch all programmatic ad slots and fall back to an native ad in case they return empty.
          .addEventListener('slotRenderEnded', renderNativeAdFallback)
      })
    } catch (err) {
      logger.error('Error on mount ad functions listeners', err)
    }
  }

  function removeEventListener() {
    if (!window.googletag.pubads) {
      logger.error('removeEventListener: Gtag pubads not found')
      return
    }
    window.googletag
      .pubads()
      .removeEventListener('slotRenderEnded', renderNativeAdFallback)
  }

  if (conf) {
    conf.onMounted(addEventListener)
    conf.onUnmounted(removeEventListener)
  }
  return {
    addEventListener,
    removeEventListener,
  }
}

export function getFallbackPlacementWidget(
  placement: string
): OutbrainProps | undefined {
  const placementConfigs = useAppConfig().adops?.placements

  if (!placementConfigs) {
    logger.error('No outbrain widgets found')
    return
  }

  if (!placementConfigs || !placementConfigs[placement]?.outbrain) {
    console.error('No outbrain widget found for: ', placement)
    return
  }

  // return regular placement name so its targeted for removal in classList
  return { ...placementConfigs[placement].outbrain, fallback: true, placement }
}

// This function is used to get the Outbrain widget ID for a specific placement.
// Used to set the dynamic price for the placement.
export function getOutbrainWidgetIdForPlacement(
  placement: string
): string | undefined {
  const widgets = useAppConfig().outbrain?.mapPlacementToWidget

  if (!widgets) {
    logger.error('No mapping for placement to outbrain widgets found.')
    return
  }

  if (!widgets || !widgets[placement]) {
    logger.error('No outbrain widget found for: ', placement)
    return
  }

  return widgets[placement]
}

export function getIndexOfPlacement(
  placement: string,
  el: Element | null
): number {
  const placementDivs = document.querySelectorAll(`.${placement}`)

  if (placementDivs.length > 1 && el) {
    const index = Array.from(placementDivs).findIndex((div) => div === el)
    if (index !== -1) {
      return index
    }
  }
  return 0
}
